import React, { useMemo, useState } from 'react';
import { Navigate, Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  KeyRound, Lock, BookOpen, Plus, Copy, Check, AlertTriangle, Trash2, Loader2, RefreshCw, ShieldAlert, Info, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import PageHeader from '../components/PageHeader';
import usePageTitle from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { r16pUiEnabled } from '../lib/r14Switch';
import { r16qUiOn } from '../lib/r16qSwitch';
import { slotsApi } from '../lib/api';
import { API_BASE } from '../lib/apiBase';
import { chatsKeys } from '../lib/chatsKeys';
import useChatsOwner from '../hooks/useChatsOwner';
import { apiKeysApi, chatsApi } from '../lib/chatsApi';
import { slotKey, parseSlotRanges, collapseToRanges } from '../lib/slotSelection';
import { classifyChatsError, chatsErrorText, chatLog, normaliseInfo, fmtWhen, CHAT_ERROR_WORDS } from '../lib/chatsView';
import {
  SCOPES, EXPIRY_OPTIONS, normaliseKeyList, keyState, keyDisplay, keyFromCreate, createBody,
} from '../lib/apiKeysView';

/*
 * r16p (2026-10-24), DESIGN D:/NecroWork/dm-chats-evidence/DESIGN.md 7.4 (api-security.md 4): /settings/api "API access", tabs in the
 * URL (/settings/api/keys | lock | docs, like /inventory/:tab).
 *
 *   Keys   the account's keys (name, nm_live_<id>_...<last 4>, scopes, slot scope, created, last used, expires) and a create dialog
 *          (scopes as checkboxes - "Queue replies" unticked with its warning -, the slot scope with the Slots page's range grammar,
 *          the expiry). The new key is shown ONCE in DesktopClientsDialog's panel (pages/DevicesPageNew.js), kept in React state only
 *          (a plain useMutation: never the query cache, never localStorage, a URL, a toast or a console line), and cleared on
 *          "Done" or close. Revoke asks first.
 *   Lock   "Lock API access": revokes every key, signs other sessions out of the chat pages and emails the account - asks first.
 *   Docs   the routes, curl examples with a PLACEHOLDER key, limits and cursors, the cadence note, HELD and release, newlines not
 *          supported yet. Every number comes from the server's answers; a missing number leaves its line out.
 * V3 never calls the public API itself (no in-browser "try it": a key is a credential for its own chain).
 * Switch: lib/r14Switch.js r16pUiEnabled (OFF: the route goes to the dashboard).
 */

const TABS = [
  { value: 'keys', label: 'Keys', icon: KeyRound },
  { value: 'lock', label: 'Lock API access', icon: Lock },
  { value: 'docs', label: 'Docs', icon: BookOpen },
];
const DIALOG = 'w-[calc(100vw-2rem)] sm:w-full max-h-[90vh] overflow-y-auto rounded-2xl border-border bg-card';
const PLACEHOLDER_KEY = 'nm_live_xxxxxxxxxx_YOUR_SECRET';

/*
 * Review: the Docs tab showed a hard-coded production address when the server sent no baseUrl. Now the server's baseUrl wins; without
 * it the address is DERIVED from where this dashboard's own API lives (lib/apiBase.js: an absolute REACT_APP_API_BASE's origin, else
 * this page's origin - the public API is /api/public/v1 on the same server as /uiapi) and the page says it was derived.
 */
export function derivedPublicBase(apiBase = API_BASE, origin = (typeof window !== 'undefined' && window.location ? window.location.origin : '')) {
  try {
    if (/^https?:\/\//i.test(apiBase || '')) return `${new URL(apiBase).origin}/api/public/v1`;
  } catch (e) { /* fall through */ }
  return origin ? `${origin}/api/public/v1` : '/api/public/v1';
}

const KEY_STATE_CHIP = {
  active: 'border-success/30 bg-success/10 text-success',
  revoked: 'border-border bg-muted/40 text-muted-foreground',
  expired: 'border-warning/30 bg-warning/10 text-warning',
};

function copyText(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) return navigator.clipboard.writeText(text);
  return new Promise((resolve, reject) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      resolve();
    } catch (e) { reject(e); }
  });
}

// ------------------------------------------------------------------------------------------------ Keys

function CreateKeyDialog({ open, onOpenChange, slotNames, atLimit, expiryDays }) {
  const queryClient = useQueryClient();
  const owner = useChatsOwner();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState(() => SCOPES.filter((s) => s.default).map((s) => s.value));
  const [allSlots, setAllSlots] = useState(true);
  const [slotExpr, setSlotExpr] = useState('');
  const [expiry, setExpiry] = useState('');
  const [issued, setIssued] = useState(null); // the one-time key: React state only
  const [copied, setCopied] = useState(false);
  const parsed = useMemo(() => (allSlots ? null : parseSlotRanges(slotExpr, slotNames)), [allSlots, slotExpr, slotNames]);

  // a plain mutation, no setQueryData: the answer (with its key) never enters the query cache. Review K1: and not the MUTATION cache
  // either - the mutationFn returns only {key, scopes count} (never the axios response), Done / close call create.reset(), and
  // gcTime 0 drops the settled mutation as soon as nothing observes it (components/ChatsCacheGuard.js also drops it on sign-out)
  const create = useMutation({
    mutationKey: [...chatsKeys.apiKeys(owner), 'create'],
    gcTime: 0,
    mutationFn: (body) => apiKeysApi.create(body).then((res) => {
      const d = res && res.data;
      // r16pm: the r16p server answers {key, shownOnce, apiKey: <the new key's row>} - the scopes are in apiKey
      const sc = d && Array.isArray(d.scopes) ? d.scopes : (d && d.apiKey && Array.isArray(d.apiKey.scopes) ? d.apiKey.scopes : null);
      return { key: keyFromCreate(d), scopes: sc ? sc.length : null };
    }),
    onSuccess: (out) => {
      const key = out && out.key;
      setIssued(key || '');
      setCopied(false);
      queryClient.invalidateQueries({ queryKey: chatsKeys.apiKeys(owner) });
      toast.success('Key created');
      chatLog('info', 'api key created', { scopes: out ? out.scopes : null, shaped: !!key });
    },
    onError: (e) => {
      const c = classifyChatsError(e);
      chatLog('warn', 'api key refused', { status: c.status, code: c.code });
      toast.error(chatsErrorText(e, 'The key could not be created'));
    },
  });

  const reset = () => {
    setIssued(null); setCopied(false); setName(''); setSlotExpr(''); setAllSlots(true); setExpiry('');
    setScopes(SCOPES.filter((s) => s.default).map((s) => s.value));
    create.reset();
  };
  const close = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };
  const submit = (e) => {
    e.preventDefault();
    if (!scopes.length) { toast.error('Tick at least one permission.'); return; }
    if (!allSlots && (!parsed || !parsed.names.length)) { toast.error('Type the slots this key may see, for example 801-805, 901.'); return; }
    create.mutate(createBody({ name, scopes, allSlots, slotNames: parsed ? parsed.names : [], expiresDays: expiry }));
  };
  const toggleScope = (v, on) => setScopes((cur) => (on ? Array.from(new Set([...cur, v])) : cur.filter((x) => x !== v)));
  const copy = async () => {
    if (!issued) return;
    try { await copyText(issued); setCopied(true); setTimeout(() => setCopied(false), 2000); toast.success('Key copied'); } catch (e) { toast.error('Could not copy - select and copy the key by hand'); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className={cn(DIALOG, 'sm:max-w-lg')} data-testid="api-key-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><KeyRound className="h-5 w-5 text-primary" />New API key</DialogTitle>
          <DialogDescription className="text-pretty">
            For your own platform or AI: it reads this account&apos;s chats and, if you allow it, queues replies. It works only on the Chats
            API, never on the dashboard.
          </DialogDescription>
        </DialogHeader>

        {issued === null ? (
          <form onSubmit={submit} className="space-y-4 py-1" data-testid="api-key-form">
            <div className="space-y-1.5">
              <Label htmlFor="api-key-name">Name</Label>
              <Input id="api-key-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} placeholder="My CRM"
                className="bg-secondary/60 border-border" data-testid="api-key-name" />
            </div>
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Permissions</legend>
              {SCOPES.map((s) => {
                const on = scopes.includes(s.value);
                return (
                  <div key={s.value} className="rounded-xl bg-secondary/50 p-3">
                    <label className="flex cursor-pointer items-start gap-3">
                      <Checkbox checked={on} onCheckedChange={(c) => toggleScope(s.value, c === true)} data-testid={`api-scope-${s.value}`} className="mt-0.5" />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium">{s.label} <code className="ml-1 font-mono text-[11px] text-muted-foreground">{s.value}</code></span>
                        <span className="block text-xs text-muted-foreground">{s.help}</span>
                      </span>
                    </label>
                    {s.warning && on && (
                      <p className="mt-2 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 p-2 text-xs text-warning" role="status" data-testid={`api-scope-warning-${s.value}`}>
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />{s.warning}
                      </p>
                    )}
                  </div>
                );
              })}
            </fieldset>
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Slots</legend>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => setAllSlots(true)} aria-pressed={allSlots} data-testid="api-slots-all"
                  className={cn('rounded-full px-3 py-1.5 text-xs font-medium ring-1', allSlots ? 'bg-primary/20 text-primary ring-primary/30' : 'text-muted-foreground ring-border')}>All slots</button>
                <button type="button" onClick={() => setAllSlots(false)} aria-pressed={!allSlots} data-testid="api-slots-some"
                  className={cn('rounded-full px-3 py-1.5 text-xs font-medium ring-1', !allSlots ? 'bg-primary/20 text-primary ring-primary/30' : 'text-muted-foreground ring-border')}>Only these slots</button>
              </div>
              {!allSlots && (
                <div className="space-y-1">
                  <Input value={slotExpr} onChange={(e) => setSlotExpr(e.target.value)} placeholder="801-805, 901" className="bg-secondary/60 border-border font-mono"
                    aria-label="Slots this key may see" data-testid="api-slots-expr" />
                  <p className="text-xs text-muted-foreground" data-testid="api-slots-readback">
                    {parsed && parsed.names.length
                      ? `${parsed.names.length} slot${parsed.names.length === 1 ? '' : 's'}: ${collapseToRanges(parsed.names)}`
                      : 'Slot numbers or ranges, as on the Slots page.'}
                    {parsed && parsed.unknown.length ? ` - ${parsed.unknown.length} not on this account were skipped` : ''}
                  </p>
                </div>
              )}
            </fieldset>
            <div className="space-y-1.5">
              <Label htmlFor="api-key-expiry">Expires</Label>
              <select id="api-key-expiry" value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="api-key-expiry"
                className="h-10 w-full rounded-xl border border-border bg-secondary/60 px-3 text-sm text-foreground">
                {expiryOptions(expiryDays).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" className="tap" onClick={() => close(false)}>Cancel</Button>
              <Button type="submit" className="tap" disabled={create.isPending || atLimit} data-testid="api-key-create">
                {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}Create key
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-3 rounded-xl border border-warning/30 bg-warning/10 p-4" data-testid="api-key-once">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
              <div className="text-sm">
                <p className="font-medium">Copy this key now - it is shown only once.</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  The server keeps only a hash. If you lose it, revoke the key and create a new one. Never put it in a URL: a key sent in a
                  URL is refused and revoked at once.
                </p>
              </div>
            </div>
            {issued ? (
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 break-all rounded-lg border border-border bg-background px-3 py-2 font-mono text-xs text-primary" data-testid="api-key-issued">{issued}</code>
                <Button size="icon" onClick={copy} className="tap h-9 w-9 shrink-0" aria-label="Copy key" data-testid="api-key-copy">
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            ) : (
              <p className="text-sm text-danger" data-testid="api-key-missing">The server created the key but its answer carried no key in the expected form. Revoke it and try again.</p>
            )}
            <p className="text-xs text-foreground/90">Send it as <code className="font-mono">Authorization: Bearer &lt;key&gt;</code> to the Chats API (see Docs).</p>
            <Button variant="outline" size="sm" className="tap" onClick={() => close(false)} data-testid="api-key-done">Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KeysTab({ list, listQ, slotNames, now, noAccessText = null }) {
  const queryClient = useQueryClient();
  const owner = useChatsOwner();
  const [createOpen, setCreateOpen] = useState(false);
  const [askRevoke, setAskRevoke] = useState(null);
  const revoke = useMutation({
    mutationFn: (id) => apiKeysApi.revoke(id),
    onSuccess: (_r, id) => {
      chatLog('info', 'api key revoked', { key: id });
      toast.success('Key revoked. It stopped working immediately.');
      queryClient.invalidateQueries({ queryKey: chatsKeys.apiKeys(owner) });
    },
    onError: (e) => toast.error(chatsErrorText(e, 'The key could not be revoked')),
  });
  const keys = list ? list.keys : [];
  const active = keys.filter((k) => keyState(k, now) === 'active').length;
  const atLimit = !!(list && Number.isFinite(list.maxActive) && active >= list.maxActive);
  // r16pm: with the server's chats switched off (chatsEnabled false) a create answers 404 chats_disabled; list / revoke / Lock stay
  const chatsOff = !!(list && list.enabled === false) || !!noAccessText;

  return (
    <div className="space-y-4" data-testid="api-tab-keys">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground" data-testid="api-keys-count">
          {active} active key{active === 1 ? '' : 's'}{list && Number.isFinite(list.maxActive) ? ` of ${list.maxActive}` : ''}.
          {' '}A key only works on the Chats API, and only for the slots you give it.
        </p>
        <Button onClick={() => setCreateOpen(true)} disabled={atLimit || !list || chatsOff} className="tap rounded-full" data-testid="api-key-new">
          <Plus className="h-4 w-4" />New key
        </Button>
      </div>
      {noAccessText && (
        <p className="flex items-start gap-2 rounded-xl border border-border bg-card/60 p-3 text-sm text-muted-foreground" role="status" data-testid="api-keys-no-access">
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          {noAccessText} No new key can be created from it; you can still revoke the keys below or lock API access.
        </p>
      )}
      {chatsOff && !noAccessText && (
        <p className="flex items-start gap-2 rounded-xl border border-border bg-card/60 p-3 text-sm text-muted-foreground" role="status" data-testid="api-keys-chats-off">
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          Chats are switched off on this server, so no new key can be created. Your keys are listed below; you can still revoke them or lock API access.
        </p>
      )}
      {listQ.isLoading ? (
        <div className="space-y-2" aria-busy="true">{[0, 1].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-secondary/50" />)}</div>
      ) : keys.length === 0 ? (
        <div className="aurora-card aurora-card-dashed p-8 text-center text-sm text-muted-foreground" data-testid="api-keys-empty">
          No API keys yet. Create one for each platform or AI that should read your chats; revoke it the moment you stop using it.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="api-keys-list">
          {keys.map((k) => {
            const st = keyState(k, now);
            return (
              <li key={k.id} className="aurora-card p-3 sm:p-4" data-testid={`api-key-row-${k.id}`} data-state={st}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2 font-medium">
                      <span className="truncate">{k.name}</span>
                      <span className={cn('rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize leading-none', KEY_STATE_CHIP[st])}>{st}</span>
                    </p>
                    <code className="mt-0.5 block truncate font-mono text-xs text-muted-foreground" data-testid={`api-key-display-${k.id}`}>{keyDisplay(k)}</code>
                  </div>
                  {st === 'active' && (
                    <Button variant="ghost" size="sm" className="tap h-8 rounded-full text-muted-foreground hover:bg-danger/10 hover:text-danger"
                      onClick={() => setAskRevoke(k)} disabled={revoke.isPending} data-testid={`api-key-revoke-${k.id}`} aria-label={`Revoke ${k.name}`}>
                      <Trash2 className="h-4 w-4" />Revoke
                    </Button>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {k.scopes.map((s) => (
                    <span key={s} className={cn('rounded-full border px-2 py-0.5 font-mono text-[10px] leading-none',
                      s === 'replies:write' ? 'border-warning/35 bg-warning/10 text-warning' : 'border-border bg-muted/40 text-muted-foreground')}>{s}</span>
                  ))}
                  <span className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[10px] leading-none text-muted-foreground">
                    {k.allSlots ? 'All slots' : `${k.slots.length || k.slotCount || 0} slots${k.slots.length ? `: ${collapseToRanges(k.slots, 4)}` : ''}`}
                  </span>
                </div>
                <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-0.5 text-xs text-muted-foreground xs:grid-cols-3">
                  <div><dt className="inline">Created </dt><dd className="inline text-foreground/85">{k.createdAt ? fmtWhen(k.createdAt, now) : '-'}</dd></div>
                  <div><dt className="inline">Last used </dt><dd className="inline text-foreground/85">{k.lastUsedAt ? fmtWhen(k.lastUsedAt, now) : 'never'}</dd></div>
                  <div><dt className="inline">{st === 'revoked' ? 'Revoked ' : 'Expires '}</dt>
                    <dd className="inline text-foreground/85">{st === 'revoked' ? (k.revokedAt ? fmtWhen(k.revokedAt, now) : '-') : (k.expiresAt ? fmtWhen(k.expiresAt, now) : 'never')}</dd></div>
                </dl>
              </li>
            );
          })}
        </ul>
      )}
      <CreateKeyDialog open={createOpen} onOpenChange={setCreateOpen} slotNames={slotNames} atLimit={atLimit || chatsOff} expiryDays={list ? list.expiryDays : null} />
      <AlertDialog open={!!askRevoke} onOpenChange={(o) => { if (!o) setAskRevoke(null); }}>
        <AlertDialogContent data-testid="api-revoke-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke &quot;{askRevoke ? askRevoke.name : ''}&quot;?</AlertDialogTitle>
            <AlertDialogDescription>It stops working immediately, for every platform that uses it. This cannot be undone; create a new key instead.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="api-revoke-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-danger text-white hover:bg-danger/90" data-testid="api-revoke-ok"
              onClick={() => { const k = askRevoke; setAskRevoke(null); if (k) revoke.mutate(k.id); }}>Revoke</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ Lock

function LockTab() {
  const queryClient = useQueryClient();
  const owner = useChatsOwner();
  const [ask, setAsk] = useState(false);
  const [done, setDone] = useState(null);
  const lock = useMutation({
    mutationFn: () => apiKeysApi.lockAccess(),
    onSuccess: (res) => {
      // r16pm: the r16p server answers {locked, keysRevoked, otherSessionsSignedOut}
      const d = res && res.data ? res.data : null;
      const raw = d ? (d.keysRevoked !== undefined && d.keysRevoked !== null ? d.keysRevoked : d.revoked) : null;
      const n = raw !== null && raw !== undefined && Number.isFinite(Number(raw)) ? Number(raw) : null;
      setDone({ revoked: n });
      chatLog('info', 'api access locked', { revoked: n });
      toast.success('API access locked.');
      queryClient.invalidateQueries({ queryKey: chatsKeys.apiKeys(owner) });
    },
    onError: (e) => toast.error(chatsErrorText(e, 'API access could not be locked')),
  });
  return (
    <div className="aurora-card max-w-2xl space-y-3 p-5" data-testid="api-tab-lock">
      <p className="flex items-center gap-2 font-heading text-base font-semibold"><ShieldAlert className="h-5 w-5 text-danger" />Lock API access</p>
      <p className="text-sm text-muted-foreground text-pretty">
        Use this if a key may have leaked, or if your dashboard password may be known to someone else (it is also in every worker PC&apos;s
        configuration). Every API key of this account stops working at once, every other signed-in session loses access to the chat
        pages, and the account is emailed. Create new keys afterwards for the integrations you still trust.
      </p>
      {done && (
        <p className="rounded-xl border border-success/30 bg-success/10 p-3 text-sm text-success" role="status" data-testid="api-lock-done">
          Locked{done.revoked !== null ? `: ${done.revoked} key${done.revoked === 1 ? '' : 's'} revoked` : ''}.
        </p>
      )}
      <Button className="tap rounded-full bg-danger text-white hover:bg-danger/90" onClick={() => setAsk(true)} disabled={lock.isPending} data-testid="api-lock-btn">
        {lock.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}Lock API access
      </Button>
      <AlertDialog open={ask} onOpenChange={setAsk}>
        <AlertDialogContent data-testid="api-lock-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Lock API access now?</AlertDialogTitle>
            <AlertDialogDescription>
              Every API key of this account is revoked at once, other sessions are signed out of the chat pages, and the account is
              emailed. Integrations that use a key stop working until you give them a new one.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="api-lock-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-danger text-white hover:bg-danger/90" data-testid="api-lock-ok" onClick={() => { setAsk(false); lock.mutate(); }}>
              Lock API access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ Docs

const ROUTES = [
  ['GET', '/me', 'any', 'The calling key, its scopes and its limits.'],
  ['GET', '/slots', 'chats:read', 'The slots this key can see (cursor-paged).'],
  ['GET', '/slots/{slot_id}', 'chats:read', 'One slot, with its last inbox read.'],
  ['GET', '/slots/{slot_id}/conversations', 'chats:read', 'Its conversations, newest activity first (updated_since, unread_only).'],
  ['GET', '/conversations/{id}', 'chats:read', 'One conversation, with replies_allowed and the reason.'],
  ['GET', '/conversations/{id}/messages', 'chats:read', 'Messages in thread order (seq); text is null after the retention period.'],
  ['POST', '/conversations/{id}/replies', 'replies:write', 'Queue a reply (Idempotency-Key header required).'],
  ['GET', '/slots/{slot_id}/replies', 'chats:read', 'Replies of a slot and their status.'],
  ['GET', '/replies/{id}', 'chats:read', 'One reply and its status.'],
  ['DELETE', '/replies/{id}', 'replies:write', 'Cancel a reply while it is still queued.'],
  ['POST', '/replies/{id}/release', 'replies:write', 'Send a held reply anyway, or confirm the person.'],
  ['DELETE', '/conversations/{id}', 'chats:delete', 'Delete a conversation and everything stored for it.'],
  ['GET', '/events', 'chats:read', 'The pull feed, oldest first (after=<cursor>).'],
  ['GET', '/openapi.yaml', 'none', 'The full contract (OpenAPI 3.1).'],
];

function Code({ children, testid }) {
  return (
    <pre className="overflow-x-auto rounded-xl border border-border bg-background/80 p-3 font-mono text-[12px] leading-relaxed text-foreground/90" data-testid={testid}>
      {children}
    </pre>
  );
}

function DocsTab({ base, baseDerived, limits, info }) {
  const L = limits || {};
  const lines = [];
  if (Number.isFinite(L.perKeyPerMinute)) lines.push(`${L.perKeyPerMinute} requests a minute per key`);
  if (Number.isFinite(L.perAccountPerMinute)) lines.push(`${L.perAccountPerMinute} a minute per account, all keys together`);
  const page = [];
  if (Number.isFinite(L.pageLimit)) page.push(`${L.pageLimit} items a page by default`);
  if (Number.isFinite(L.pageMax)) page.push(`at most ${L.pageMax} with limit=`);
  const maxLen = Number.isFinite(L.maxReplyLength) ? L.maxReplyLength : (info && info.maxReplyLength);
  const retention = Number.isFinite(L.retentionDays) ? L.retentionDays : (info && info.retentionDays);
  return (
    <div className="max-w-4xl space-y-5" data-testid="api-tab-docs">
      <section className="aurora-card space-y-2 p-5">
        <h3>Not real time</h3>
        <p className="text-sm text-muted-foreground text-pretty" data-testid="api-docs-cadence">
          {(info && info.cadenceNote) || 'Messages reach the server when a slot\'s session reads its inbox, so they arrive minutes to hours after they were sent. A queued reply is sent by the phone at the slot\'s next session, at human pace.'}
        </p>
      </section>
      <section className="aurora-card space-y-2 p-5">
        <h3>Base URL and authentication</h3>
        <Code testid="api-docs-base">{base}</Code>
        {baseDerived && (
          <p className="text-xs text-muted-foreground" data-testid="api-docs-base-derived">
            The server did not send its API address; this one is derived from where this dashboard&apos;s own API lives.
          </p>
        )}
        <p className="text-sm text-muted-foreground">Send the key in a header, never in a URL (a key in a URL is refused and revoked):</p>
        <Code>{`Authorization: Bearer ${PLACEHOLDER_KEY}`}</Code>
      </section>
      <section className="aurora-card space-y-3 p-5">
        <h3>Routes</h3>
        {/* phones: one card per route; from sm up: the table */}
        <ul className="space-y-2 sm:hidden" data-testid="api-docs-routes-list">
          {ROUTES.map(([m, p, sc, w]) => (
            <li key={`${m} ${p}`} className="rounded-xl border border-border/60 bg-background/40 p-2.5">
              <p className="flex flex-wrap items-baseline gap-x-2 font-mono text-xs"><span className="text-primary">{m}</span><span className="break-all">{p}</span></p>
              <p className="mt-0.5 text-xs text-muted-foreground">{w} <span className="font-mono text-[11px]">({sc})</span></p>
            </li>
          ))}
        </ul>
        <div className="hidden overflow-x-auto sm:block">
          <table className="w-full min-w-[34rem] text-left text-sm" data-testid="api-docs-routes">
            <thead><tr className="text-xs uppercase tracking-wide text-muted-foreground"><th className="py-1.5 pr-3">Method</th><th className="py-1.5 pr-3">Path</th><th className="py-1.5 pr-3">Scope</th><th className="py-1.5">What it does</th></tr></thead>
            <tbody>
              {ROUTES.map(([m, p, s, w]) => (
                <tr key={`${m} ${p}`} className="border-t border-border/50 align-top">
                  <td className="py-1.5 pr-3 font-mono text-xs text-primary">{m}</td>
                  <td className="py-1.5 pr-3 font-mono text-xs">{p}</td>
                  <td className="py-1.5 pr-3 font-mono text-[11px] text-muted-foreground">{s}</td>
                  <td className="py-1.5 text-xs text-muted-foreground">{w}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <a href={`${base}/openapi.yaml`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline" data-testid="api-docs-openapi">
          The full contract (openapi.yaml)<ExternalLink className="h-3.5 w-3.5" aria-hidden />
        </a>
      </section>
      <section className="aurora-card space-y-2 p-5">
        <h3>Examples</h3>
        <p className="text-sm text-muted-foreground">Read the new messages since your last cursor:</p>
        <Code testid="api-docs-curl-events">{`curl -s "${base}/events?after=<cursor>" \\
  -H "Authorization: Bearer ${PLACEHOLDER_KEY}"`}</Code>
        <p className="text-sm text-muted-foreground">Queue a reply in a conversation (one line of text; the Idempotency-Key makes a retry safe):</p>
        <Code testid="api-docs-curl-reply">{`curl -s -X POST "${base}/conversations/<conv_id>/replies" \\
  -H "Authorization: Bearer ${PLACEHOLDER_KEY}" \\
  -H "Idempotency-Key: <a new uuid per reply>" \\
  -H "Content-Type: application/json" \\
  -d '{"text": "Thanks, we will get back to you today."}'`}</Code>
      </section>
      <section className="aurora-card space-y-2 p-5">
        <h3>Replies</h3>
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground text-pretty" data-testid="api-docs-replies">
          <li data-testid="api-docs-window">
            A reply goes only into a conversation the worker has read,{' '}
            {Number.isFinite(L.replyWindowDays) ? `within ${L.replyWindowDays} days of the other person's last message` : 'while the other person\'s last message is recent enough (the server checks the window)'}.
            {' '}The API cannot start a conversation.
          </li>
          <li>States: queued, claimed, sending, held, sent, unconfirmed, failed, cancelled. An unconfirmed reply is never sent again on its own.</li>
          <li><b>held</b>: newer messages arrived after the reply was queued (hold_reason new_inbound), or the person must be confirmed first (confirm_peer). Nothing is sent until you call <code className="font-mono">POST /replies/{'{id}'}/release</code> or cancel it.</li>
          {/* r16q (the r16pdc README section 4 item 6; necro.ui.r16q.apiHelp): the merged server's two reply fields, in the words of its
              openapi-v1.yaml (Reply.waiting_reason / waiting_message, fail_code inbound_out_of_view, fail_message); no number of ours */}
          {r16qUiOn('apiHelp') && (
            <li data-testid="api-docs-waiting">
              <code className="font-mono">waiting_reason</code> (with <code className="font-mono">waiting_message</code>, the sentence for people): set only on
              a queued reply that waits because of its conversation&apos;s folder, else null. The worker does not open or type into it until it is read in
              Primary or General again; if that does not happen before <code className="font-mono">expires_at</code>, the reply
              fails <code className="font-mono">expired</code>. Such replies do not count toward the slot&apos;s limit of waiting replies.
              <ul className="mt-1 list-[circle] space-y-0.5 pl-5" data-testid="api-docs-waiting-codes">
                <li><code className="font-mono">request_not_ruled_out</code> = the conversation was last read in a list that also shows message requests
                  (Instagram&apos;s All tab) or in an unproven tab;</li>
                <li><code className="font-mono">request_not_accepted</code> = it is now in the message requests.</li>
              </ul>
            </li>
          )}
          {r16qUiOn('apiHelp') && (
            <li data-testid="api-docs-fail-message">
              <code className="font-mono">fail_message</code>: a sentence for people for some fail codes, else null; for other codes use your own words.
              <ul className="mt-1 list-[circle] space-y-0.5 pl-5" data-testid="api-docs-fail-codes">
                <li><code className="font-mono">inbound_out_of_view</code>: the person&apos;s last message is too far up the chat for the worker to check. On
                  a <code className="font-mono">failed</code> reply it is final and nothing was sent: reply by hand on the phone. On
                  an <code className="font-mono">unconfirmed</code> reply Send may have been tapped; it is never re-sent automatically.</li>
              </ul>
            </li>
          )}
          <li>Newlines are not supported yet: line breaks become spaces, and the reply carries the text as it will be typed.{Number.isFinite(maxLen) ? ` At most ${maxLen} characters.` : ''}</li>
          <li data-testid="api-docs-repeat">
            The same text again in a conversation{Number.isFinite(L.repeatWindowHours) ? ` within ${L.repeatWindowHours} hours` : ' recently'}, or to many conversations, is refused unless you mean it (allow_repeat).
          </li>
        </ul>
      </section>
      <section className="aurora-card space-y-2 p-5">
        <h3>Limits and cursors</h3>
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground" data-testid="api-docs-limits">
          {lines.length > 0 && <li>{lines.join('; ')}. Over the limit: 429 with RateLimit-* headers.</li>}
          <li>Lists are cursor-paged: <code className="font-mono">{'{data, next_cursor, has_more}'}</code>{page.length ? `; ${page.join(', ')}` : ''}.</li>
          {Number.isFinite(L.eventsDays) && <li>The events feed keeps {L.eventsDays} days; an older cursor answers 410 cursor_expired.</li>}
          {Number.isFinite(retention) && <li>Message text is kept {retention} days, then removed (text becomes null).</li>}
          <li>Errors: <code className="font-mono">{'{error: {code, message, request_id}}'}</code>; an object outside the key&apos;s slots answers 404.</li>
        </ul>
      </section>
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ the page

export default function ApiSettingsPage() {
  usePageTitle('API access');
  const on = r16pUiEnabled();
  const { tab: tabParam } = useParams();
  const tab = TABS.some((t) => t.value === tabParam) ? tabParam : 'keys';
  const [now] = useState(() => Date.now());

  const owner = useChatsOwner();
  const listQ = useQuery({ queryKey: chatsKeys.apiKeys(owner), queryFn: () => apiKeysApi.list().then((r) => r.data), enabled: on, retry: false });
  const list = useMemo(() => (listQ.data ? normaliseKeyList(listQ.data) : null), [listQ.data]);
  const listErr = listQ.error ? classifyChatsError(listQ.error) : null;
  const infoQ = useQuery({ queryKey: chatsKeys.info(owner), queryFn: () => chatsApi.info().then((r) => r.data), enabled: on, retry: false, staleTime: 300000 });
  const info = useMemo(() => (infoQ.data ? normaliseInfo(infoQ.data) : null), [infoQ.data]);
  // the merged server's 403 no_chat_access (SERVER-CONTRACT.md 2d): the key LIST, revoke and Lock stay reachable, a create is refused
  const noAccess = !!(infoQ.error && classifyChatsError(infoQ.error).kind === 'noAccess');
  const noAccessText = noAccess ? (classifyChatsError(infoQ.error).message || CHAT_ERROR_WORDS.no_chat_access) : null;
  const slotsQ = useQuery({ queryKey: ['slots'], queryFn: () => slotsApi.list().then((r) => r.data), enabled: on && tab === 'keys', staleTime: 300000 });
  const slotNames = useMemo(() => (Array.isArray(slotsQ.data) ? slotsQ.data.map(slotKey).filter(Boolean) : []), [slotsQ.data]);

  if (!on) return <Navigate to="/dashboard" replace />;
  if (tabParam && tab !== tabParam) return <Navigate to="/settings/api/keys" replace />;

  // the key list's numbers win over the info's, but a number one of them did not send (null) never hides the other's
  const limits = {};
  // r16pm: the r16p server's info carries replyWindowDays / maxReplyLength / retentionDays at its TOP level (no `api` block); an
  // `api` block (a later server) and the key list's numbers still win over it
  const infoTop = info ? { replyWindowDays: info.replyWindowDays, maxReplyLength: info.maxReplyLength, retentionDays: info.retentionDays } : {};
  [infoTop, (info && info.api && typeof info.api === 'object') ? normaliseInfoLimits(info.api) : {}, (list && list.limits) || {}].forEach((src) => {
    Object.keys(src).forEach((k) => { if (src[k] !== null && src[k] !== undefined) limits[k] = src[k]; });
  });
  const serverBase = (list && list.baseUrl) || (info && info.api && typeof info.api.baseUrl === 'string' && info.api.baseUrl ? info.api.baseUrl : null);
  const base = serverBase || derivedPublicBase();

  const header = (
    <PageHeader icon={KeyRound} title="API access" back={{ to: '/settings', label: 'Settings' }}
      description="Let your own platform or AI read your slots' chats and queue replies, with keys you can scope and revoke." />
  );

  if (listErr && listErr.kind === 'routeMissing') {
    return (
      <div data-testid="api-page" data-state="route-missing">
        {header}
        <div className="aurora-card aurora-card-dashed p-8 text-center text-sm text-muted-foreground" data-testid="api-no-route">
          This server has no Chats API yet (it needs the r16p server).
        </div>
      </div>
    );
  }
  if (listErr && listErr.kind === 'notEnabled') {
    return (
      <div data-testid="api-page" data-state="off">
        {header}
        <p className="flex items-start gap-2 rounded-xl border border-border bg-card/60 p-4 text-sm text-muted-foreground" data-testid="api-off">
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />{listErr.message || 'The Chats API is switched off on this server.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="api-page" data-state="ready" data-tab={tab}>
      {header}
      <nav className="flex flex-wrap gap-1 rounded-full border border-white/10 bg-secondary/40 p-1 w-fit max-w-full" aria-label="API access">
        {TABS.map((t) => (
          <Link key={t.value} to={`/settings/api/${t.value}`} aria-current={tab === t.value ? 'page' : undefined} data-testid={`api-tab-link-${t.value}`}
            className={cn('inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
              tab === t.value ? 'bg-primary/20 text-primary ring-1 ring-primary/30' : 'text-muted-foreground hover:text-foreground')}>
            <t.icon className="h-4 w-4" aria-hidden />{t.label}
          </Link>
        ))}
      </nav>
      {listErr && listErr.kind !== 'routeMissing' && tab === 'keys' && (
        <div className="aurora-card p-5 text-sm" role="alert" data-testid="api-error">
          <p className="text-muted-foreground">{chatsErrorText(listQ.error, 'The keys could not be loaded')}</p>
          <Button variant="outline" size="sm" className="tap mt-3 rounded-full" onClick={() => listQ.refetch()}><RefreshCw className="h-4 w-4" />Try again</Button>
        </div>
      )}
      {tab === 'keys' && !listErr && <KeysTab list={list} listQ={listQ} slotNames={slotNames} now={now} noAccessText={noAccessText} />}
      {tab === 'lock' && <LockTab />}
      {tab === 'docs' && <DocsTab base={base} baseDerived={!serverBase} limits={limits} info={info} />}
    </div>
  );
}

/** The expiry choices: Never, then the server's expiryDays (ApiKeyService.EXPIRY_DAYS) when it sent them, else 90 / 365. */
function expiryOptions(days) {
  if (!Array.isArray(days) || !days.length) return EXPIRY_OPTIONS;
  return [EXPIRY_OPTIONS[0], ...days.map((d) => ({ value: String(d), label: `In ${d} days` }))];
}

function normaliseInfoLimits(api) {
  const n = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
  return {
    perKeyPerMinute: n(api.perKeyPerMinute), perAccountPerMinute: n(api.perAccountPerMinute), pageLimit: n(api.pageLimit),
    pageMax: n(api.pageMax), eventsDays: n(api.eventsDays), replyWindowDays: n(api.replyWindowDays), repeatWindowHours: n(api.repeatWindowHours),
  };
}
